import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { initTheme } from './lib/theme';
import { installShellPrompt } from './lib/shellPrompt';
import './index.css';
import InvoicePage from './order/InvoicePage';

// v0.9.688: route-level code splitting. Every surface used to be imported
// eagerly, so ONE ~2MB bundle shipped to every visitor - a staffer checking
// their schedule downloaded the whole POS + admin. Each surface is now its
// own chunk; a route only downloads what it renders. (No sync-code change:
// this only moves WHEN module code loads, same as a slower device.)
const App = lazy(() => import('./App'));
const MenuBoard = lazy(() => import('./board/MenuBoard'));
const StatusBoard = lazy(() => import('./board/StatusBoard'));
const MarketingSite = lazy(() => import('./marketing/MarketingSite'));
const SiteEditor = lazy(() => import('./marketing/SiteEditor'));
const TechFiles = lazy(() => import('./marketing/TechFiles'));
const PrivacyPolicy = lazy(() => import('./marketing/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./marketing/TermsOfService'));
const NextTabSms = lazy(() => import('./marketing/NextTabSms'));
const KbShared = lazy(() => import('./marketing/KbShared'));
const Storefront = lazy(() => import('./order/Storefront'));
const Kiosk = lazy(() => import('./order/Kiosk'));
const Wallet = lazy(() => import('./order/Wallet'));
const ManagerSuite = lazy(() => import('./portal/ManagerSuite'));
const JoinLoyalty = lazy(() => import('./order/JoinLoyalty'));
const GiftCard = lazy(() => import('./order/GiftCard'));
const StaffSchedule = lazy(() => import('./order/StaffSchedule'));
const GuestPay = lazy(() => import('./order/GuestPay'));
const ReceiptPage = lazy(() => import('./order/ReceiptPage'));
const Onboard = lazy(() => import('./order/Onboard'));

// v0.9.286: install the saved theme's CSS variables before first paint so the
// app never flashes the fallback palette.
initTheme();

const root = document.getElementById('root');
if (!root) throw new Error('root element not found');

// Path-based routing (no router library; Netlify serves index.html for all
// paths via the /* -> /index.html 200 rule):
//   /board/...  -> standalone TV menu board (no POS app, no auth)
//   /status/... -> standalone pickup status board (no POS app, no auth)
//   /           -> public marketing site (v0.9.316)
//   /register   -> the POS app (login / pair / run the floor)
//   anything else -> POS app (back-compat, e.g. deep links)
const path = window.location.pathname;
// v0.9.1508: the marketing site editor. Online-only, platform-admin
// gated, and deliberately NOT given the POS offline shell.
const isSiteEditor = path === '/site-editor' || path.startsWith('/site-editor/');
// v0.9.1887: install-tech file page. /files (and /ftp-files, any case).
const isTechFiles = /^\/(files|ftp-files)(\/|$)/i.test(path);
const isBoard = path.startsWith('/board/');
const isStatus = path.startsWith('/status/');
const isOrder = path.startsWith('/order/');
const isKiosk = path.startsWith('/kiosk/');
const isJoin = path.startsWith('/join/');
const isGift = path.startsWith('/gift/');
const isStaffSchedule = path.startsWith('/schedule/');
const isGuestPay = path.startsWith('/pay/');
// v0.9.1828: a texted receipt, /receipt/<token>. Public by token.
const isReceipt = path.startsWith('/receipt/');
// v0.9.1482: new-hire onboarding form, /onboard/<token>. Public by token; no device, no SW.
const isOnboard = path.startsWith('/onboard/');
// v0.9.1416: the customer-facing house account invoice, /invoice/<token>.
const isInvoice = path.startsWith('/invoice/') || path.startsWith('/statement/');
const isWallet = path.startsWith('/wallet');
// v0.9.1357: the manager web suite. Matches /managersuite and
// /managersuite/<slug>, the same slug-carrying shape /order, /schedule and
// /kiosk already use.
const isManagerSuite = path === '/managersuite' || path.startsWith('/managersuite/');

// v0.9.1457: index.css hides scrollbars everywhere for touch terminals.
// The suite is a desktop mouse page, so it opts back in - done here,
// before React mounts, so the bar is present from first paint rather
// than popping in when the lazy chunk lands.
if (isManagerSuite) document.documentElement.classList.add('suite-scrollbars');

/*
 * v0.9.2033: the POS app never scrolls the PAGE.
 *
 * iOS Safari rubber-bands the document on any drag, even when everything
 * already fits on screen - the whole UI slides up, snaps back, and the tap
 * that was meant for a button lands during the snap and is lost. The fix is
 * structural: html/body are locked (no overflow), and #root becomes the one
 * scroll container with overscroll-behavior none, which iOS honours on a
 * scroll container but not on the document. A screen that fits has nothing
 * to scroll, so nothing moves; a long screen still scrolls inside #root.
 * Only the POS app (App) - the public pages and the manager suite keep
 * ordinary page scrolling.
 */
const isPosApp = !(isBoard || isStatus || isOrder || isJoin || isGift || isStaffSchedule || isGuestPay || isReceipt
  || isOnboard || isInvoice || isWallet || isManagerSuite || isKiosk || isSiteEditor || isTechFiles);

// v0.9.344: kiosk PWA launch guard. iOS Safari is unreliable about reading a
// runtime-swapped manifest at "Add to Home Screen" time - it often bakes in the
// page's static manifest (POS, start_url:/register), so an installed kiosk icon
// would open the POS. To make the installed icon reliably open the kiosk
// regardless of what start_url iOS captured:
//   1) Remember the kiosk slug whenever a kiosk page is visited.
//   2) On boot, if we're running as an INSTALLED standalone PWA and a kiosk slug
//      was remembered, but we did NOT launch on a kiosk path, redirect to it.
// A POS install never records nt.kioskSlug, so it is unaffected.
const KIOSK_SLUG_KEY = 'nt.kioskSlug';
function isStandalonePwa(): boolean {
  // iOS exposes navigator.standalone; other browsers use display-mode.
  const nav = navigator as unknown as { standalone?: boolean };
  const mm = typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
  return nav.standalone === true || mm;
}
(function kioskLaunchGuard(): void {
  try {
    if (isKiosk) {
      const m = path.match(/\/kiosk\/([^/?#]+)/);
      if (m) localStorage.setItem(KIOSK_SLUG_KEY, decodeURIComponent(m[1]));
      return; // already on the kiosk; nothing to redirect
    }
    const slug = localStorage.getItem(KIOSK_SLUG_KEY);
    if (slug && isStandalonePwa()) {
      // Installed kiosk icon launched somewhere else (e.g. /register). Bounce.
      window.location.replace('/kiosk/' + encodeURIComponent(slug));
    }
  } catch {
    // localStorage unavailable - ignore; kiosk still works as a browser tab.
  }
})();

// Treat exactly "/" (and empty) as the marketing root. Everything else that
// isn't the board renders the POS app, so /register and any other path keep
// working as the app.
const isMarketingRoot = path === '/' || path === '';
// v0.9.1302: the privacy policy must be a real, publicly reachable URL - it is
// linked from app stores, merchant underwriting and the Google Business
// Profile API application, all of which check that it actually loads.
const isPrivacy = path === '/privacy' || path === '/privacy/';
// v0.9.1839: terms of service - the T&Cs URL carriers ask for next to the privacy policy.
const isTerms = path === '/terms' || path === '/terms/';
// v0.9.2012: opt in to texts from NextTab (owners, managers, staff) - the 10DLC opt-in form.
const isNextTabSms = path === '/sms' || path === '/sms/';
// v0.9.2022: a shared knowledge base article (nexttabpos.com/kb/<token>).
const isKbShared = /^\/kb\/[A-Za-z0-9_-]{32}\/?$/.test(path);
if (isPosApp && !(isMarketingRoot || isPrivacy || isTerms || isNextTabSms || isKbShared)) {
  document.documentElement.classList.add('pos-locked');
  /*
   * v0.9.2046: the locked page must also stay at scroll 0. iOS still scrolls
   * the document to "reveal" a focused field (overflow: hidden does not stop
   * it), which leaves the app drawn offset from where taps land - keys and
   * buttons hit the thing above or below them. Snap it back whenever it moves.
   */
  /*
   * v0.9.2051: NOT while the phone's own keyboard is typing into a field.
   * iOS moves the page to lift that field above its keyboard - pinning undid
   * it and the field sat hidden under the keys. The built-in keyboard marks
   * its fields inputmode="none"; those (and nothing focused) still pin.
   */
  const nativeTyping = (): boolean => {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const editable = el.tagName === 'TEXTAREA' || el.isContentEditable
      || (el.tagName === 'INPUT' && !/^(button|submit|reset|checkbox|radio|range|color|file|image|hidden)$/i.test((el as HTMLInputElement).type));
    return editable && (el.getAttribute('inputmode') || '').toLowerCase() !== 'none';
  };
  const pin = (): void => {
    if (nativeTyping()) return;
    if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0);
    if (document.documentElement.scrollTop) document.documentElement.scrollTop = 0;
    if (document.body && document.body.scrollTop) document.body.scrollTop = 0;
  };
  window.addEventListener('scroll', pin, { passive: true });
  document.addEventListener('focusin', () => { pin(); window.setTimeout(pin, 50); window.setTimeout(pin, 300); });
  // Keyboard put away: put the page back where it belongs.
  document.addEventListener('focusout', () => { window.setTimeout(pin, 60); window.setTimeout(pin, 400); });
}

// v0.9.343: when this is a kiosk page, swap the POS manifest for a kiosk one
// whose start_url is THIS kiosk (slug included) and scope is /kiosk/. iOS reads
// the manifest at "Add to Home Screen" time, so an installed kiosk icon then
// launches straight into kiosk mode instead of falling through to the POS at
// /register. Done at runtime because start_url must carry the live slug.
function installKioskManifest(): void {
  const m = path.match(/\/kiosk\/([^/?#]+)/);
  const slug = m ? decodeURIComponent(m[1]) : '';
  const startUrl = '/kiosk/' + encodeURIComponent(slug);
  const manifest = {
    name: 'Order Kiosk',
    short_name: 'Order',
    description: 'Self-order kiosk',
    start_url: startUrl,
    scope: '/kiosk/',
    display: 'standalone',
    orientation: 'any',
    background_color: '#11100e',
    theme_color: '#11100e',
    icons: [
      { src: '/icon-192.png', sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: '/icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: '/icon-512-maskable.png', sizes: '512x512', type: 'image/png', purpose: 'maskable' },
    ],
  };
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
  const url = URL.createObjectURL(blob);
  const link = document.querySelector('link[rel="manifest"]');
  if (link) link.setAttribute('href', url);
  // Match the installed-app title + status bar to the kiosk, not the POS.
  const appTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appTitle) appTitle.setAttribute('content', 'Order Kiosk');
  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.setAttribute('content', '#11100e');
  document.title = 'Order Kiosk';
}
if (isKiosk) installKioskManifest();

// v0.9.316: only the POS app should run as an installed PWA. Marketing theme
// init still applies; the app reads its own theme on mount.
function pickRoot(): JSX.Element {
  if (isBoard) return <MenuBoard />;
  if (isStatus) return <StatusBoard />;
  if (isOrder) return <Storefront />;
  if (isJoin) return <JoinLoyalty />;
  if (isGift) return <GiftCard />;
  if (isStaffSchedule) return <StaffSchedule />;
  if (isGuestPay) return <GuestPay />;
  if (isReceipt) return <ReceiptPage />;
  if (isOnboard) return <Onboard />;
  if (isInvoice) return <InvoicePage />;
  if (isWallet) return <Wallet />;
  if (isManagerSuite) return <ManagerSuite />;
  if (isKiosk) return <Kiosk />;
  if (isPrivacy) return <PrivacyPolicy />;
  if (isTerms) return <TermsOfService />;
  if (isNextTabSms) return <NextTabSms />;
  if (isKbShared) return <KbShared />;
  if (isSiteEditor) return <SiteEditor />;
  if (isTechFiles) return <TechFiles />;
  if (isMarketingRoot) return <MarketingSite />;
  return <App />;
}

// v0.9.1643: window.prompt works inside the Windows shell - see shellPrompt.
installShellPrompt();

createRoot(root).render(
  <StrictMode>
    <Suspense fallback={<div style={{ minHeight: '100vh', background: '#0c1118' }} />}>
      {pickRoot()}
    </Suspense>
  </StrictMode>
);

// v0.9.874: register the app-shell service worker so the POS can BOOT offline
// (power outage -> reboot -> no internet yet). The SW precaches the static
// shell and is network-only for /v1/ + the websocket (never caches data).
//
// Scope: ONLY the POS app, never the public surfaces. The storefront (/order,
// /s/), kiosk (/kiosk), menu board (/board), guest pay, join-loyalty, gift
// card, and staff schedule (/schedule - which owns its own push SW) are all
// online-only customer/staff pages that don't need offline boot and must not
// have their SW scope clobbered. Failures are non-fatal.
if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  const p = window.location.pathname;
  // Public/online-only surfaces that should NOT get the offline app-shell SW.
  // Prefixes mirror the route detection above (board/order/kiosk/join/gift/
  // schedule/pay), plus the marketing root "/". Everything else (/register and
  // deep links) is the POS app and gets offline boot.
  const isPublicSurface =
    p === '/' || p === '' ||
    // v0.9.1357: managersuite is an online-only back-office page. Giving it
    // the POS offline shell would cache a signed-out shell for a surface
    // that has nothing useful to do without the network anyway.
    p === '/managersuite' ||
    p === '/site-editor' ||
    /^\/(files|ftp-files)(\/|$)/i.test(p) ||
    /^\/(sms|privacy|terms)\/?$/.test(p) ||
    /^\/(board|status|order|kiosk|join|gift|schedule|pay|managersuite|onboard|invoice|statement|site-editor)\//.test(p);
  if (!isPublicSurface) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/app-sw.js', { scope: '/' }).catch(() => {
        /* offline-boot just won't be available; app still works online */
      });
    });
  }
}
